import { useEffect, useState } from "react";
import type { SystemSettings } from "@gooner/shared";
import { DEFAULT_SETTINGS } from "@gooner/shared";
import { api } from "./api";

/**
 * The public settings block, fetched ONCE per page load and shared by every reader.
 *
 * Lived inside gold.ts as a private promise until FAPS needed the same answer; one
 * fetch, one cache, so two gates on one page cannot disagree about which switches are
 * on. Falls back to DEFAULT_SETTINGS on failure — each reader decides what its own
 * permissive direction is (Gold: allow; FAPS: hide), this just hands over the object.
 *
 * Cached for the page's life on purpose: a settings flip reaches an open tab on its
 * next load, which the admin panel says out loud beside every switch.
 */
let settingsPromise: Promise<SystemSettings> | null = null;

export const publicSettings = (): Promise<SystemSettings> =>
  (settingsPromise ??= api.getSettings().catch(() => DEFAULT_SETTINGS));

/**
 * True when `s` is the failure fallback rather than a real read. For the reader whose
 * safe direction is NOT the default: AuthModal's GOONCHA box, where DEFAULT says "off"
 * but an unreadable flag must still show the box (a login the server gates on a token
 * we never offered to mint is a lockout). The fallback is the DEFAULT_SETTINGS object
 * itself, and a parsed response never is, so identity is the whole test.
 */
export const settingsUnread = (s: SystemSettings): boolean => s === DEFAULT_SETTINGS;

/** The settings, or null until the one fetch answers. */
export function usePublicSettings(): SystemSettings | null {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  useEffect(() => {
    let cancelled = false;
    void publicSettings().then((s) => !cancelled && setSettings(s));
    return () => {
      cancelled = true;
    };
  }, []);
  return settings;
}
